import React, { useEffect, useState } from 'react';
import { Result, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useSnapshot } from 'valtio';
import { Outlet, useNavigate, useLocation } from 'react-router';
import { AuthStore } from '@/utils/store/auth';
import { supabaseAuthStore } from '@/utils/store/supabaseAuth';

const AuthLayout: React.FC = () => {
  // 只使用 Supabase 认证状态
  const supabaseAuth = useSnapshot(supabaseAuthStore);
  const { data: authData, isLoading: authLoading } = useSnapshot(AuthStore);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState('analysis');

  // 权限检查
  useEffect(() => {
    if (supabaseAuth.isAuthenticated && supabaseAuth.user?.email) {
      AuthStore.runFetch(supabaseAuth.user.email);
    }
  }, [supabaseAuth.isAuthenticated, supabaseAuth.user?.email]);

  // 根据当前路径设置选中的菜单项
  useEffect(() => {
    if (location.pathname.includes('/debug')) {
      setSelectedKey('debug');
    } else if (location.pathname.includes('/iframe')) {
      setSelectedKey('iframe');
    } else if (location.pathname.includes('/asr-compare')) {
      setSelectedKey('asr-compare');
    } else if (location.pathname.includes('/superadmin')) {
      setSelectedKey('superadmin');
    } else if (location.pathname.includes('/tech')) {
      setSelectedKey('tech');
    } else if (location.pathname.includes('/analysis')) {
      setSelectedKey('analysis');
    }
  }, [location.pathname]);

  // 如果正在加载权限信息或者还没有权限数据，显示鉴权中
  if (authLoading || authData === null) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <div className="loading loading-spinner loading-lg mb-4"></div>
        <div className="text-lg text-gray-600">鉴权中...</div>
      </div>
    );
  }

  // 如果不是超级管理员，显示无权限页面
  if (!authData.isSuperAdmin) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="抱歉，您没有权限访问此页面。只有超级管理员才能查看历史记录分析。"
        icon={<LockOutlined />}
        extra={
          <Button type="primary" onClick={() => window.history.back()}>
            返回上一页
          </Button>
        }
      />
    );
  }

  const handleMenuClick = (key: string) => {
    setSelectedKey(key);
    if (key === 'analysis') {
      navigate('/analysis');
    } else if (key === 'iframe') {
      navigate('/analysis/iframe');
    } else if (key === 'asr-compare') {
      navigate('/analysis/asr-compare');
    } else if (key === 'debug') {
      navigate('/analysis/debug');
    } else if (key === 'superadmin') {
      navigate('/analysis/superadmin');
    } else if (key === 'tech') {
      navigate('/analysis/tech');
    }
  };

  // 有权限，渲染带菜单栏的布局
  return (
    <div className="flex flex-col gap-4">
      {/* DaisyUI Navbar */}
      <div className="navbar bg-base-100 border-b border-base-200 px-6 h-10 rounded-xl">
         <div className="flex items-center w-full">
           <div className="text-lg font-bold text-primary mr-8 flex-shrink-0">
             管理后台
           </div>
           <div className="tabs tabs-boxed bg-base-200 flex items-center flex-1 overflow-x-auto">
            <button
              className={`tab ${selectedKey === 'analysis' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('analysis')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              数据分析
            </button>
            <button
              className={`tab ${selectedKey === 'asr-compare' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('asr-compare')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
              ASR转写对比
            </button>
            <button
              className={`tab ${selectedKey === 'iframe' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('iframe')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9a9 9 0 00-9 9m-9 9a9 9 0 019-9" />
              </svg>
              腾讯BI
            </button>
            <button
              className={`tab ${selectedKey === 'debug' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('debug')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              工作流调试
            </button>
            <button
              className={`tab ${selectedKey === 'superadmin' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('superadmin')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              超级管理员
            </button>
            <button
              className={`tab ${selectedKey === 'tech' ? 'tab-active' : ''}`}
              onClick={() => handleMenuClick('tech')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              技术分析
            </button>
          </div>
        </div>
      </div>
      
      {/* 主要内容区域 */}
      <div className="">
        <Outlet />
      </div>
    </div>
  );
};

export default AuthLayout;